import { useState } from "react";
import { Loader2, PiggyBank, Wallet } from "lucide-react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePartyCreditBalance } from "@/hooks/queries/usePartyCreditBalance";
import { useSubscribeSavingsMutation } from "@/hooks/mutations/useSubscribeSavings";
import { useConvertCreditToEpargneMutation } from "@/hooks/mutations/useConvertCreditToEpargne";
import { ResponsibleAgentSelect } from "@/components/ResponsibleAgentSelect";
import { useAuth } from "@/App";

interface SubscribeButtonProps {
  partyId: number;
  onSubscribed: () => void;
}

type Step = "no-credit" | "choice" | "amount";

// Both subscription paths create employee-stats activity, so both go
// through a confirmation dialog with a responsible-employee picker rather
// than a silent one-click (2026-10-02 correction — the no-credit path used
// to skip straight to the mutation). When the party HAS available credit,
// opening the account is also no longer an obviously-correct one-click
// action (2026-09-23 audit: it used to silently ignore the credit and
// charge the inscription fee separately), so that path asks first too.
export function SubscribeButton({ partyId, onSubscribed }: SubscribeButtonProps) {
  const { data: creditBalanceData } = usePartyCreditBalance(partyId);
  const availableCredit = creditBalanceData?.balance ?? 0;

  const { user } = useAuth();
  const canPickResponsible = user ? ["manager", "admin", "super_admin"].includes(user.role) : false;

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("choice");
  const [amount, setAmount] = useState(0);
  const [responsibleAgentId, setResponsibleAgentId] = useState<number | undefined>(undefined);

  const subscribeMutation = useSubscribeSavingsMutation(partyId);
  const convertMutation = useConvertCreditToEpargneMutation(partyId);

  function reset() {
    setStep(availableCredit > 0 ? "choice" : "no-credit");
    setAmount(0);
    setResponsibleAgentId(undefined);
  }

  function handleSubscribe() {
    subscribeMutation.mutate(responsibleAgentId, {
      onSuccess: () => {
        setOpen(false);
        reset();
        onSubscribed();
      },
    });
  }

  function handleUseCredit() {
    convertMutation.mutate(
      { amount, responsibleAgentId },
      {
        onSuccess: () => {
          setOpen(false);
          reset();
          onSubscribed();
        },
      },
    );
  }

  if (availableCredit <= 0) {
    return (
      <Dialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (v) setStep("no-credit");
          else reset();
        }}
      >
        <DialogTrigger>
          <Button size="sm">
            <PiggyBank size={13} /> Ouvrir un compte épargne
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ouvrir le compte épargne</DialogTitle>
            <DialogDescription>
              L'activité sera comptabilisée dans les statistiques de cet employé. L'audit conserve
              l'utilisateur qui saisit l'opération.
            </DialogDescription>
          </DialogHeader>
          {canPickResponsible && (
            <ResponsibleAgentSelect value={responsibleAgentId} onChange={setResponsibleAgentId} helperText="" />
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubscribe} disabled={subscribeMutation.isPending}>
              {subscribeMutation.isPending ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                "Confirmer l'ouverture"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
      }}
    >
      <DialogTrigger>
        <Button size="sm">
          <PiggyBank size={13} /> Ouvrir un compte épargne
        </Button>
      </DialogTrigger>
      <DialogContent>
        {step === "choice" ? (
          <>
            <DialogHeader>
              <DialogTitle>Crédit disponible détecté</DialogTitle>
              <DialogDescription>
                Ce client dispose d'un avoir disponible ({availableCredit.toLocaleString("fr-FR")} XAF).
                Voulez-vous utiliser tout ou partie de cet avoir pour ouvrir le compte épargne ?
              </DialogDescription>
            </DialogHeader>
            {canPickResponsible && (
              <ResponsibleAgentSelect value={responsibleAgentId} onChange={setResponsibleAgentId} />
            )}
            <DialogFooter>
              <Button variant="secondary" onClick={() => setOpen(false)}>
                Annuler
              </Button>
              <Button
                variant="outline"
                onClick={handleSubscribe}
                disabled={subscribeMutation.isPending}
              >
                {subscribeMutation.isPending ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  "Ouvrir sans utiliser l'avoir"
                )}
              </Button>
              <Button
                onClick={() => {
                  setAmount(availableCredit);
                  setStep("amount");
                }}
              >
                <Wallet size={13} /> Utiliser l'avoir
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Utiliser l'avoir</DialogTitle>
              <DialogDescription>
                Montant à convertir en épargne — maximum {availableCredit.toLocaleString("fr-FR")} XAF.
              </DialogDescription>
            </DialogHeader>
            <div>
              <label className="block text-[11.5px] font-medium text-body mb-1.5">Montant</label>
              <Input
                type="number"
                value={amount || ""}
                onChange={(e) =>
                  setAmount(Math.min(Math.max(parseFloat(e.target.value) || 0, 0), availableCredit))
                }
                max={availableCredit}
                autoFocus
              />
            </div>
            {canPickResponsible && (
              <ResponsibleAgentSelect value={responsibleAgentId} onChange={setResponsibleAgentId} />
            )}
            <DialogFooter>
              <Button variant="secondary" onClick={() => setStep("choice")}>
                Retour
              </Button>
              <Button
                onClick={handleUseCredit}
                disabled={convertMutation.isPending || amount <= 0 || amount > availableCredit}
              >
                {convertMutation.isPending ? <Loader2 size={13} className="animate-spin" /> : "Confirmer"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
